import type { ChildProfile, AdultProfile } from '@yoyoclub/shared';
import { useTranslation } from 'react-i18next';

interface SelectedProfileCardProps {
  profile: ChildProfile | AdultProfile;
  type: 'child' | 'adult';
  onRemove: () => void;
}

/**
 * Displays a selected profile card with key information and remove button
 * Used in booking checkout to show selected participants
 */
export default function SelectedProfileCard({ profile, type, onRemove }: SelectedProfileCardProps) {
  const { t } = useTranslation('booking');

  // Calculate age for child profiles
  const getAge = (birthDate: Date): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white flex items-center justify-between hover:border-primary transition-colors">
      <div className="flex-1">
        {type === 'child' ? (
          <div>
            <div className="font-medium text-gray-900">
              {(profile as ChildProfile).preferredName || (profile as ChildProfile).name}
            </div>
            <div className="text-sm text-gray-600">
              {t('components.selectedProfile.age')} {getAge((profile as ChildProfile).birthDate)}
            </div>
            {(profile as ChildProfile).notes && (
              <div className="text-xs text-gray-500 mt-1">
                {t('components.selectedProfile.note')}: {(profile as ChildProfile).notes}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="font-medium text-gray-900">{(profile as AdultProfile).name}</div>
            <div className="text-sm text-gray-600">{(profile as AdultProfile).phone}</div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="ml-4 text-gray-400 hover:text-red-600 transition-colors"
        aria-label={t('components.selectedProfile.removeAria')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}
